import React, { lazy, PropsWithChildren } from 'react';
import {
  Outlet,
  Navigate,
  RouterProvider,
  createBrowserRouter,
  RouteObject as RouterRouteObject,
} from 'react-router-dom';

import { NotFound } from 'pages';
import { AppRoutes } from 'config';
import { Role, Feature } from 'types';
import { useAppSelector } from 'store';
import { RouteObject, getRouteObjects } from 'config/route-objects';
import { Login, ResetPassword, SendResetPassword } from 'pages/auth';
import {
  HeaderContextProvider,
  SocketContextProvider,
  SidebarContextProvider,
} from 'context';
import {
  selectIsLoggedIn,
  selectAuthUserRole,
  selectAuthUserFeatures,
} from 'store/slices/auth';

const AppPrivate = lazy(() => import('./app-private'));
// Public, no sign-in, no app chrome: the manager's Off-today link.
const OffToday = lazy(() => import('pages/off-today'));
// Public, no sign-in: the rep's onboarding link.
const Onboarding = lazy(() => import('pages/onboarding'));

const AuthLayout = () => {
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  return isLoggedIn ? <Navigate to={AppRoutes.Home} /> : <Outlet />;
};

export const ProtectedRoute = () => {
  const isLoggedIn = useAppSelector(selectIsLoggedIn);

  return isLoggedIn ? (
    <SidebarContextProvider>
      <HeaderContextProvider>
        <SocketContextProvider>
          <AppPrivate />
        </SocketContextProvider>
      </HeaderContextProvider>
    </SidebarContextProvider>
  ) : (
    <Navigate to={AppRoutes.Auth.Login} />
  );
};

export const FeatureProtectedRoute = ({
  children,
  features,
}: PropsWithChildren<{ features: Feature[] }>) => {
  const authUserFeatures = useAppSelector(selectAuthUserFeatures);

  if (!features.some((permission) => authUserFeatures?.includes(permission))) {
    return <Navigate to={AppRoutes.Home} />;
  }

  return children || <Outlet />;
};

const RoleProtectedRoute = ({
  children,
  hideForRoles,
}: PropsWithChildren<{ hideForRoles: Role[] }>) => {
  const userRole = useAppSelector(selectAuthUserRole);

  if (userRole && hideForRoles.includes(userRole)) {
    return <Navigate to={AppRoutes.Home} />;
  }

  return children || <Outlet />;
};

const RootRedirect = () => {
  const userRole = useAppSelector(selectAuthUserRole);
  const authUserFeatures = useAppSelector(selectAuthUserFeatures);

  const firstRoute = getRouteObjects().find(
    ({
      features,
      tripsOnly,
      myTripOnly,
      bookingOnly,
      researchOnly,
      hideForRoles,
      trainingOnly,
      repPerformanceOnly,
    }) => {
      // TIME OFF, BOOKING, REP PERFORMANCE, TRAINING, TRIPS: never the landing
      // page.
      if (
        bookingOnly ||
        researchOnly ||
        repPerformanceOnly ||
        trainingOnly ||
        tripsOnly ||
        myTripOnly ||
        (userRole && hideForRoles?.includes(userRole))
      ) {
        return false;
      }
      // Payroll and Time off became ticks (owner 2 Nov 2026) but stay off
      // the landing page, as before.
      if (
        features?.includes(Feature.PAYROLL) ||
        features?.includes(Feature.TIME_OFF)
      ) {
        return false;
      }
      if (features) {
        return features.some((f) => authUserFeatures?.includes(f));
      }
      return true;
    }
  );

  return <Navigate replace to={firstRoute?.path || AppRoutes.Home} />;
};

const mapToRouterRoute = (route: RouteObject): RouterRouteObject => {
  let element = route.element;

  if (route.hideForRoles?.length) {
    element = (
      <RoleProtectedRoute hideForRoles={route.hideForRoles}>
        {element}
      </RoleProtectedRoute>
    );
  }

  if (route.features?.length) {
    element = (
      <FeatureProtectedRoute features={route.features}>
        {element}
      </FeatureProtectedRoute>
    );
  }

  return {
    element,
    path: route.path,
    index: route.index,
    children: route.children?.map(mapToRouterRoute),
  } as RouterRouteObject;
};

const router = createBrowserRouter([
  {
    path: AppRoutes.Home,
    element: <ProtectedRoute />,
    children: [
      {
        index: true,
        element: <RootRedirect />,
      },
      ...getRouteObjects().map(mapToRouterRoute),
      {
        path: '*',
        element: <NotFound homePath={AppRoutes.Home} />,
      },
    ],
  },
  {
    path: AppRoutes.NotFound,
    element: <NotFound homePath={AppRoutes.Home} />,
  },
  {
    element: <OffToday />,
    path: AppRoutes.OffToday,
  },
  {
    element: <Onboarding />,
    path: AppRoutes.Onboarding,
  },
  {
    element: <AuthLayout />,
    children: [
      { element: <Login />, path: AppRoutes.Auth.Login },
      { element: <SendResetPassword />, path: AppRoutes.Auth.ForgotPassword },
      { element: <ResetPassword />, path: AppRoutes.Auth.ResetPassword },
    ],
  },
]);

const Router = () => <RouterProvider router={router} />;

export default Router;
